import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CustomHeader } from "../components/AuthHeader";
import { CustomButton } from "../components/CustomButton";
import { AppCredits } from "../components/AppCredits";
import { useAuthStore } from "../features/auth/authStore";
import { authService } from "../features/auth/authService";
import { ArrowLeft, Eye, EyeOff, ChevronRight } from "lucide-react";
import { useToast } from "../components/ToastProvider";
import rushLogo from "../assets/png/rush-logo.png";

export default function SingUpScreen() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const isLoading = useAuthStore((s) => s.isLoading);
  const [sending, setSending] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSignUp = async () => {
    if (isLoading || sending) return;

    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !phone.trim() ||
      !email.trim() ||
      !password.trim()
    ) {
      showToast("Please fill all fields", "error");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      showToast("Enter a valid email address", "error");
      return;
    }

    if (password.length < 8) {
      showToast("Password must be at least 8 characters", "error");
      return;
    }

    if (password !== confirmPassword) {
      showToast("Passwords do not match", "error");
      return;
    }

    try {
      const cleanedPhone = phone.trim();
      const registrationData = {
        firstName,
        lastName,
        email: email.trim(),
        password,
        phone: cleanedPhone,
        role: "customer",
      } as const;

      // Confirm the email first: we send a 6-digit code, the account is
      // created on the next screen once the code is entered
      setSending(true);
      await authService.requestSignupCode(registrationData.email, firstName.trim());
      showToast("We've emailed you a 6-digit code", "success");
      navigate("/verify", { state: { registrationData } });
    } catch (err: any) {
      showToast(err?.message || "Registration failed", "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col bg-white px-6 pt-4 pb-10 min-h-screen">
      <div className="sticky top-0 z-50 bg-white/90 backdrop-blur-md px-6 -mx-6 pt-4 pb-4 flex items-center justify-between">
        <button
          onClick={() => navigate("/auth")}
          className="w-11 h-11 border border-gray-100 rounded-2xl flex items-center justify-center active:scale-95 transition-transform shadow-sm bg-white"
        >
          <ArrowLeft size={20} className="text-gray-700" />
        </button>

        <img
          src={rushLogo}
          alt="Rush Logo"
          className="h-7 w-auto object-contain"
        />
      </div>

      <CustomHeader
        title="Create Your Account"
        subtitle="Register once to start placing laundry orders easily."
      />

      {/* Form Area - removed flex-1 to keep items grouped */}
      <div className="mt-8 space-y-5">
        <div>
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-2 block">
            First Name
          </label>
          <input
            type="text"
            placeholder="Enter your name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full h-12 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-gray-800 outline-none focus:border-[#23A9C6] focus:bg-white transition-all"
          />
        </div>

        <div>
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-2 block">
            Last Name
          </label>
          <input
            type="text"
            placeholder="Enter your name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="w-full h-12 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-gray-800 outline-none focus:border-[#23A9C6] focus:bg-white transition-all"
          />
        </div>

        <div>
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-2 block">
            Mobile / WhatsApp
          </label>
          <input
            type="tel"
            placeholder="Enter your number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full h-12 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-gray-800 outline-none focus:border-[#23A9C6] focus:bg-white transition-all"
          />
        </div>

        <div>
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-2 block">
            Email Address
          </label>
          <input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full h-12 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-gray-800 outline-none focus:border-[#23A9C6] focus:bg-white transition-all"
          />
        </div>

        <div>
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-2 block">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-12 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-gray-800 outline-none focus:border-[#23A9C6] focus:bg-white transition-all"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <div>
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-2 block">
            Confirm Password
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Enter Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full h-12 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-gray-800 outline-none focus:border-[#23A9C6] focus:bg-white transition-all"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <div className="mt-10 mb-4 text-center text-sm text-gray-500 animate-in fade-in slide-in-from-bottom-2">
          Already have an account?{" "}
          <button
            onClick={() => navigate("/login")}
            className="text-[#23A9C6] font-bold"
          >
            Log In
          </button>
        </div>

        <CustomButton
          variant="primary"
          disabled={isLoading || sending}
          onClick={() => void handleSignUp()}
          className="w-full h-14 text-lg rounded-2xl font-bold flex items-center justify-center gap-3 active:scale-[0.98] transition-transform shadow-lg shadow-cyan-100 mt-4"
        >
          {sending ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Sending code...</span>
            </>
          ) : (
            <span>Continue</span>
          )}
        </CustomButton>

        <AppCredits className="mt-8 text-[10px]" />
      </div>
    </div>
  );
}
